import { z } from 'zod';
import { ConflictResultSeveritySchema, ConflictResultStatusSchema } from './conflict';
import { ConsultationRecordSchema } from './consultation';
import { EngagementRecordSchema } from './engagement';
import { EmailIntakeSchema, IntakeSubmissionSchema } from './intake-payload';
import { TimestampLike } from './timestamp-like';

export const InquiryStatusSchema = z.enum([
  'new',
  'contacted',
  'need_info',
  'consulting',
  'engaging',
  'retained',
  'not_a_fit',
]);
export type InquiryStatus = z.infer<typeof InquiryStatusSchema>;

export const CreatedBySchema = z.object({
  uid: z.string(),
  email: z.string(),
  displayName: z.string().nullable(),
});
export type CreatedBy = z.infer<typeof CreatedBySchema>;

export const ManualIntakeDataSchema = z.object({
  referralSource: z.string().nullable(),
  notes: z.string().nullable(),
  enteredAt: z.custom<TimestampLike>(),
});
export type ManualIntakeData = z.infer<typeof ManualIntakeDataSchema>;

export const IntakeSessionSchema = z.strictObject({
  tokenHash: z.string(),
  firmId: z.string(),
  firmSlug: z.string().nullable(),
  status: z.enum(['active', 'submitted', 'expired', 'timed_out']),
  source: z.strictObject({
    referrer: z.string().nullable(),
    campaign: z.string().nullable(),
    requestReferrer: z.string().nullish(),
  }),
  createdAt: z.custom<TimestampLike>(),
  expiresAt: z.number(),
  lastSeenAt: z.custom<TimestampLike>(),
  telemetry: z.strictObject({
    ip: z.string().nullable(),
    origin: z.string().nullable(),
    referer: z.string().nullable(),
    userAgent: z.string().nullable(),
    lastIp: z.string().nullish(),
    lastOrigin: z.string().nullish(),
    lastReferer: z.string().nullish(),
    lastUserAgent: z.string().nullish(),
    repeatDevice: z.boolean().nullish(),
    repeatIp: z.boolean().nullish(),
    priorDeviceCount: z.number().nullish(),
    priorIpCount: z.number().nullish(),
  }),
  visitorId: z.string().nullable(),
  draftData: z.record(z.string(), z.unknown()).nullable(),
  submission: IntakeSubmissionSchema,
  submittedAt: z.custom<TimestampLike>().nullish(),
  verificationJobs: z
    .strictObject({
      email: z.strictObject({
        job_id: z.string(),
        workflow_execution: z.string(),
      }),
      phone: z.strictObject({
        job_id: z.string(),
        workflow_execution: z.string(),
      }),
    })
    .nullish(),
  signals: z.record(z.string(), z.unknown()).nullish(),
  consultations: z.array(ConsultationRecordSchema).default([]),
  inquiryStatus: InquiryStatusSchema.nullish(),
  statusChangedAt: z.custom<TimestampLike>().nullish(),
  channel: z.enum(['web_form', 'email', 'phone', 'walk_in', 'referral']).nullish(),
  emailIntake: EmailIntakeSchema.nullish(),
  createdBy: CreatedBySchema.nullish(),
  manualIntake: ManualIntakeDataSchema.nullish(),
  conflictStatus: ConflictResultStatusSchema.nullish(),
  conflictSeverity: ConflictResultSeveritySchema.nullable().nullish(),
  engagement: EngagementRecordSchema.nullish(),
});
